import React, { useState, useRef, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  CircularProgress,
  Divider,
  Grid,
  Switch,
  IconButton,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

import {
  GET_URL_METADATA,
  PUT_CUSTOM_LOGO,
  PUT_HUB_ITEM,
  DELETE_HUB_ITEM,
} from "../../api/hub";
import { UseUserId, UseIsAdmin } from "../../hooks/auth";
import { UseSelectedWorkspace, UseUsersFullData } from "../../hooks/company";
import debounce from "lodash/debounce";
import startCase from "lodash/startCase";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ConfirmationModal from "../Modals/ConfirmationModal";
import toast from "react-hot-toast";
import SoftInput from "../SoftInput";
import SoftBox from "../SoftBox";
import SoftSelect from "../SoftSelect";
import SoftButton from "../SoftButton";
// Imported Images and Icons
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import AddLinkIcon from "@mui/icons-material/AddLink";
import SoftTypography from "../SoftTypography";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import { GET_USER_BY_GROUPID } from "../../api/user";
import SoftDialog from "../SoftConfirmationDialog";
import AssignLinkSection from "./AssignLinkSection";
import HubLogoCropper from "./HubLogoCropper";
import { GET_IMAGE } from "../../helpers/images";
import { toDisplayText, toErrorMessage } from "../../helpers/errorText";

const NO_URL_ASSIGNED_LABEL = "No URL assigned yet";

const deriveLinkType = (linkTitle, linkType) => {
  if (linkType) return linkType;
  const slug = (linkTitle || "")
    .trim()
    .toLowerCase()
    .replace(/[^\w-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "custom-link";
};

const AddHubLink = ({
  isOpen,
  isEdit,
  onClose,
  openDialog,
  data,
  refetch,
  setDisplayTitle,
  setDisplayWeb,
}) => {
  const [url, setUrl] = useState("");
  const [logo, setLogo] = useState("");
  const [title, setTitle] = useState("");
  const [website, setWebsite] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [type, setType] = useState(undefined);
  const [desc, setDesc] = useState("");
  const [advance, setAdvance] = useState("");
  const [customImage, setCustomImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [forUpload, setForUpload] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [logoPreviewSrc, setLogoPreviewSrc] = useState(null);

  const workspace = UseSelectedWorkspace();
  const userId = UseUserId();
  const isAdmin = UseIsAdmin();
  const imageInputRef = useRef();
  const appTheme = useTheme();
  const matches = useMediaQuery(appTheme.breakpoints.up(640));
  const usersData = UseUsersFullData();

  const {
    primary: { main: primaryColor },
  } = appTheme.palette;

  const revokeCropSrc = () => {
    if (cropImageSrc) {
      URL.revokeObjectURL(cropImageSrc);
      setCropImageSrc(null);
    }
  };

  const handleClose = () => {
    setIsSaving(false);
    setType(undefined);
    setUrl("");
    setTitle("");
    setLogo("");
    setForUpload(false);
    setCustomImage(null);
    setDesc("");
    setAdvance("");
    setCropDialogOpen(false);
    revokeCropSrc();
    onClose();
  };

  const fetchHandler = useMemo(
    () =>
      debounce((urlText) => {
        if (urlText) {
          fetchHandler?.cancel();
          setIsLoading(true);
          setType(undefined);
          GET_URL_METADATA(urlText)
            .then(({ data: urlData }) => {
              const rawTitle = urlData.type || urlData.title;
              const siteTitle =
                typeof rawTitle === "string" ? startCase(rawTitle.trim()) : "";
              setWebsite(urlData.url);
              setLogo(urlData.logo);
              setTitle(siteTitle);
              setType(urlData.type);
              setDesc(urlData.desc);
              setIsLoading(false);
            })
            .catch((_) => {
              setWebsite(null);
              setLogo(null);
              setType(undefined);
              setDesc(null);
              setIsLoading(false);
              toast.error("No data found for URL  or keyword.");
            });
        }
      }, 1000),
    [],
  );

  const handleInputChange = (e) => {
    if (e.target.files.length === 0) return;
    const file = e.target.files[0];
    if (!file) return;
    revokeCropSrc();
    setCropImageSrc(URL.createObjectURL(file));
    setCropDialogOpen(true);
    e.target.value = "";
  };

  const handleCropComplete = (file) => {
    setCustomImage(file);
    setForUpload(true);
    revokeCropSrc();
    setCropDialogOpen(false);
  };

  const handleCropClose = () => {
    setCropDialogOpen(false);
    revokeCropSrc();
  };

  const handleSave = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      toast.error("Title is required.");
      return;
    }

    try {
      setIsSaving(true);
      const saveUrl = url.trim() || (typeof website === "string" ? website.trim() : "");
      const saveType = deriveLinkType(trimmedTitle, type);
      let customImageKey = customImage;
      if (forUpload && customImage) {
        const formData = new FormData();
        formData.append("image", customImage);
        customImageKey = await PUT_CUSTOM_LOGO(formData);
      }

      await PUT_HUB_ITEM(
        data?._id,
        userId,
        workspace._id,
        saveUrl,
        saveType,
        trimmedTitle,
        customImageKey?.data || logo,
        desc,
        selectedUsers,
      );
      setDisplayTitle(trimmedTitle);
      setDisplayWeb(saveUrl);
      refetch?.();
      setSelectedUsers([]);
      if (isEdit) {
        toast.success("Link has been updated.");
      } else {
        openDialog();
      }
      handleClose();
    } catch (saveErr) {
      const errMsg = toErrorMessage(
        saveErr?.response?.data?.error ?? saveErr?.response?.data ?? saveErr?.message,
        "Could not save link.",
      );
      toast.error(errMsg);
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (data._id) {
        setIsSaving(true);
        await DELETE_HUB_ITEM(data._id);
        toast.success("URL was successfully deleted");
        refetch?.();
        handleClose();
      }
    } catch (_) {
      setIsSaving(false);
      toast.error("URL was not deleted");
    }
  };

  const fetchUsersByGroupId = async () => {
    setIsFetching(true);
    try {
      const filteredUsers = usersData
        .filter((user) => user.groupId === workspace._id)
        .map((item) => {
          const username = `${item.firstName} ${item.lastName}`;

          return {
            ...item,
            _id: item.userId,
            value: item.userId,
            label: username,
          };
        });

      setUsers(filteredUsers);
    } catch (error) {
      console.log(error.response);
    }
    setIsFetching(false);
  };

  useEffect(() => {
    if (data && (data._id || data.title || data.url)) {
      setUrl(data.url || "");
      setTitle(toDisplayText(data.title));
      setWebsite(data.website || data.url || "");
      setType(data.type?.source);
      setDesc(toDisplayText(data.desc));
      setCustomImage(data.logo || data.type?.imageSrc || null);
    }
  }, [data]);

  useEffect(() => {
    fetchUsersByGroupId();
  }, [usersData]);

  useEffect(() => {
    if (!customImage) {
      setLogoPreviewSrc(null);
      return undefined;
    }
    if (customImage instanceof File) {
      const objectUrl = URL.createObjectURL(customImage);
      setLogoPreviewSrc(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    if (typeof customImage === "string") {
      setLogoPreviewSrc(
        customImage.startsWith("http") ? customImage : GET_IMAGE(customImage),
      );
    }
    return undefined;
  }, [customImage]);

  const fieldSx = {
    borderRadius: "10px",
    outline: "none !important",
    backgroundColor: "#fff !important",
    color: "#343A40 !important",
    "& .MuiInputLabel-root": {
      color: "#495057 !important",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#ADB5BD !important",
      opacity: 1,
    },
    "&.Mui-focused": {
      borderColor: primaryColor,
      boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}33`,
    },
  };

  const getDefaultValue = () => {
    if (!data || !data._id) return null;
    if (!data.assignedTo) return null;

    return data.assignedTo.map((item) => {
      const username = `${item.firstName} ${item.lastName}`;
      
      return {
        ...item,
        value: item._id,
        label: username,
      };
    });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      scroll="paper"
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          backgroundColor: "#fff",
          border: "1px solid #EAECF0",
          boxShadow:
            "0px 8px 8px rgba(16, 24, 40, 0.04), 0px 20px 24px rgba(16, 24, 40, 0.1)",
          maxHeight: "calc(100vh - 32px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      sx={{
        "& .MuiBackdrop-root": {
          background: "rgba(16, 24, 40, 0.4)",
        },
        "& .MuiPaper-root": {
          margin: { mini: "12px", sm: "24px" },
        },
      }}
    >
      <DialogContent
        sx={{
          flex: "1 1 auto",
          overflowY: "auto",
          px: { mini: 2, sm: 3 },
          pt: { mini: 2, sm: 3 },
          pb: 2,
        }}
      >
      <SoftBox
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <SoftBox
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 0.5,
          }}
        >
          <SoftBox sx={{ minWidth: 0 }}>
            <SoftTypography
              sx={{ fontFamily: "DM Sans", fontSize: "17px!important", fontWeight: "700!important", color: "#101828" }}
              component="h2"
            >
              {isEdit ? "Edit" : "Add"} custom link
            </SoftTypography>
            <SoftTypography
              variant="caption"
              sx={{ color: "#667085", display: "block", mt: 0.5 }}
            >
              {isEdit
                ? "Update title, URL, description, or logo"
                : "Add a title and optional URL — you can assign a URL later"}
            </SoftTypography>
          </SoftBox>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#667085",
              border: "1px solid #D0D5DD",
              flexShrink: 0,
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </SoftBox>

        <Divider sx={{ width: "100%", my: 2, borderColor: "#EAECF0" }} />

        <Grid container spacing={2}>
          <Grid item mini={12} xs={12}>
            <SoftBox
              sx={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              {isLoading ? (
                <CircularProgress
                  size="2rem"
                  sx={{
                    position: "absolute",
                    right: "0.5rem",
                    top: "0.25rem",
                  }}
                />
              ) : null}
              <SoftInput
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                placeholder="Title"
                label="Title"
                onChange={(e) => setTitle(e.target.value)}
                value={title}
                sx={fieldSx}
              />
              <SoftInput
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                label="URL (optional)"
                placeholder="https://example.com"
                onChange={(e) => {
                  const urlText = e.target.value.trim();
                  setUrl(urlText);
                  if (urlText) {
                    fetchHandler(urlText);
                  } else {
                    setWebsite("");
                    fetchHandler.cancel?.();
                  }
                }}
                value={url}
                sx={fieldSx}
              />
              {!url.trim() && (
                <SoftTypography
                  variant="caption"
                  sx={{
                    color: "#B54708",
                    backgroundColor: "#FFFAEB",
                    border: "1px solid #FEC84B",
                    borderRadius: "999px",
                    px: 1.25,
                    py: 0.5,
                    display: "inline-block",
                    fontSize: "11px",
                    fontWeight: 600,
                  }}
                >
                  {NO_URL_ASSIGNED_LABEL}
                </SoftTypography>
              )}
              <SoftBox>
                <SoftTypography
                  variant="caption"
                  sx={{
                    color: "#475467",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    mb: 0.75,
                    display: "block",
                    fontSize: "11px",
                  }}
                >
                  Description
                </SoftTypography>
                <SoftInput
                  placeholder="Type your description here ..."
                  multiline
                  rows={5}
                  onChange={(e) => {
                    setDesc(e.target.value);
                  }}
                  value={desc}
                  sx={fieldSx}
                />
              </SoftBox>
              <SoftBox display="flex" alignItems="flex-start" gap={2}>
                {isAdmin ?
                  <SoftBox>
                    <Switch
                      onChange={(e) => setAdvance(e.target.checked)}
                    />
                  </SoftBox>
                  : null}
                {isAdmin ?
                  <SoftBox display="flex" flexDirection="column" gap={1}>
                    <SoftTypography
                      variant="h4"
                      sx={{
                        color: "#344054",
                        fontSize: "15px",
                        fontWeight: 500,
                      }}
                    >
                      Advanced Settings
                    </SoftTypography>
                  </SoftBox>
                  : null}
              </SoftBox>

              {advance && isAdmin && (<SoftSelect
                name="isType"
                id="category"
                className="custom-select-catgory"
                options={users}
                isMulti
                onChange={(option) => {
                  const list = [...option];
                  setSelectedUsers(list);
                }}
                isLoading={fetching}
                defaultValue={getDefaultValue()}
                placeholder="Select player"
                styles={{
                  control: {
                    backgroundColor: "#fff",
                    borderColor: "#D0D5DD",
                    height: "100%",
                    minWidth: "2.5rem",
                  },
                  placeholder: {
                    color: "#98A2B3",
                  },
                  input: {
                    color: "#344054",
                  },
                  singleValue: {
                    color: "#344054",
                  },
                  valueContainer: {
                    flexFlow: "row wrap",
                    gap: "0.25rem",
                    padding: "0.5rem 0.75rem",
                  },
                  indicatorsContainer: {
                    alignItems: "start",
                  },
                  menu: {
                    zIndex: 40,
                    marginTop: "8px",
                    // Left/right split intentionally — the scrollbar
                    // already eats into the right inset, so an equal
                    // 8px both sides read as extra-wide on the left.
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    paddingLeft: "4px",
                    paddingRight: "8px",
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #EAECF0",
                    borderRadius: "12px",
                    boxShadow: "0px 8px 24px rgba(16, 24, 40, 0.12)",
                  },
                  menuList: {
                    maxHeight: "260px",
                    padding: 0,
                    // Native scrollbars run full-width on some browsers,
                    // eating far more space on the right than the 12px
                    // option padding matched on the left. A slim custom
                    // scrollbar keeps both sides visually even.
                    scrollbarWidth: "thin",
                    scrollbarColor: "#D0D5DD transparent",
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#D0D5DD",
                      borderRadius: "3px",
                    },
                  },
                  option: {
                    minWidth: 0,
                    // top/right/bottom/left — left trimmed to match
                    // menu's own left/right split above.
                    padding: "10px 12px 10px 8px",
                    color: "#344054",
                    fontSize: "14px",
                    fontWeight: 500,
                    backgroundColor: "#FFFFFF",
                    borderRadius: "8px",
                    "&[aria-selected='true']": {
                      color: primaryColor,
                      backgroundColor: `${primaryColor}14`,
                    },
                    "&:hover, &:focus": {
                      color: "#101828",
                      backgroundColor: "#F2F4F7",
                    },
                  },
                }}
              />)}

              <SoftBox sx={{ mt: 1 }}>
                <SoftTypography
                  variant="caption"
                  sx={{
                    color: "#475467",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    mb: 1,
                    display: "block",
                    fontSize: "11px",
                  }}
                >
                  Custom logo
                </SoftTypography>
                <SoftBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  {logoPreviewSrc && (
                    <SoftBox
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "12px",
                        overflow: "hidden",
                        flexShrink: 0,
                        border: "1px solid #EAECF0",
                        background: "#F9FAFB",
                        "& img": {
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        },
                      }}
                    >
                      <img src={logoPreviewSrc} alt="Logo preview" />
                    </SoftBox>
                  )}
                  <SoftButton
                    variant="outlined"
                    size={matches ? "medium" : "small"}
                    sx={{
                      minWidth: "max-content",
                      padding: "0.5rem 1rem",
                      borderRadius: "10px",
                      color: "#344054",
                      borderColor: "#D0D5DD",
                      backgroundColor: "#fff",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                        borderColor: "#98A2B3",
                      },
                    }}
                    endIcon={
                      <FileUploadRoundedIcon
                        sx={{ fontSize: "1.125rem !important", color: "#667085" }}
                      />
                    }
                    onClick={() => imageInputRef.current?.click()}
                    disabled={isLoading || isSaving}
                  >
                    {logoPreviewSrc ? "Change logo" : "Upload logo"}
                  </SoftButton>
                  <input
                    ref={imageInputRef}
                    onChange={handleInputChange}
                    accept="image/png, image/gif, image/jpeg"
                    type="file"
                    hidden
                  />
                </SoftBox>
                <SoftTypography
                  variant="caption"
                  sx={{ color: "#667085", display: "block", mt: 0.75 }}
                >
                  Square crop recommended · PNG, JPG, or GIF
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          </Grid>
        </Grid>

        {isEdit && isAdmin && data?._id && (
          <>
            <Divider sx={{ width: "100%", my: 2, borderColor: "#EAECF0" }} />
            <AssignLinkSection
              id={data._id}
              url={url || data.url}
              type={type || data.type?.source}
              assignedTo={data.assignedTo}
              onSuccess={refetch}
            />
          </>
        )}
      </SoftBox>
      </DialogContent>

      <SoftBox
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          px: { mini: 2, sm: 3 },
          py: 2,
          borderTop: "1px solid #EAECF0",
          backgroundColor: "#fff",
        }}
      >
        <SoftBox sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {!isEdit && (
            <SoftButton
              variant="outlined"
              size="medium"
              onClick={handleClose}
              sx={{
                textTransform: "none",
                color: "#344054",
                borderColor: "#D0D5DD",
                backgroundColor: "#fff",
                "&:hover": {
                  backgroundColor: "#F9FAFB",
                  borderColor: "#98A2B3",
                },
              }}
            >
              Cancel
            </SoftButton>
          )}
          {isEdit && (
            <ConfirmationModal
              title="Delete Hub Item"
              question={`Are you sure you like to delete this URL?\n${url}`}
              yesText="Yes, delete."
              noText="No, cancel"
              onSuccess={handleDelete}
            >
              <SoftButton
                variant="outlined"
                size="medium"
                color="error"
                startIcon={
                  isSaving ? <CircularProgress size={24} color="error" /> : null
                }
                sx={{
                  textTransform: "none",
                  backgroundColor: "#fff",
                }}
              >
                Delete link
              </SoftButton>
            </ConfirmationModal>
          )}
        </SoftBox>
        <SoftButton
          color="primary"
          variant="gradient"
          size="medium"
          sx={{
            textTransform: "none",
            color: "#fff",
            minWidth: { mini: "100%", sm: "140px" },
          }}
          endIcon={
            isSaving ? <CircularProgress size={24} color="inherit" /> : null
          }
          onClick={handleSave}
          disabled={isSaving || !title.trim()}
        >
          {isEdit ? "Save changes" : "Create link"}
        </SoftButton>
      </SoftBox>

      <HubLogoCropper
        open={cropDialogOpen}
        imageSrc={cropImageSrc}
        onClose={handleCropClose}
        onComplete={handleCropComplete}
      />
    </Dialog>
  );
};

export default AddHubLink;
